import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, UsersRound, Users, User, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

const SECTION_META = {
  distributors: { label: "Distributors", icon: Building2, color: "text-blue-600" },
  parties: { label: "Parties", icon: UsersRound, color: "text-emerald-600" },
  employees: { label: "Market Developers", icon: Users, color: "text-amber-600" },
  ibos: { label: "IBOs", icon: User, color: "text-purple-600" },
};

const HIGHLIGHT = (text, q) => {
  if (!text || !q) return text || "";
  const idx = String(text).toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <mark className="bg-yellow-200 rounded-sm px-0.5">{match}</mark>
      {after}
    </>
  );
};

export default function GlobalSearch({ allowedRoles = [] }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Close on click outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Keyboard shortcut: ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced fetch
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setData(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q: term, limit: 8 } });
        setData(data);
        setActiveIndex(0);
        setOpen(true);
      } catch {
        setData({ distributors: [], parties: [], employees: [], ibos: [], query: term });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const flatResults = React.useMemo(() => {
    if (!data) return [];
    const list = [];
    ["distributors", "parties", "employees", "ibos"].forEach((key) => {
      (data[key] || []).forEach((item) => list.push({ ...item, __section: key }));
    });
    return list;
  }, [data]);

  const goTo = (item) => {
    setOpen(false);
    setQ("");
    if (item.__section === "distributors") {
      navigate(`/distribution/distributors?q=${encodeURIComponent(item.code || item.name)}`);
    } else if (item.__section === "parties") {
      navigate(`/distribution/parties?q=${encodeURIComponent(item.code || item.name)}`);
    } else if (item.__section === "employees" || item.__section === "ibos") {
      navigate(`/users?q=${encodeURIComponent(item.email || item.name)}`);
    }
  };

  const onKeyDown = (e) => {
    if (!open || flatResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatResults[activeIndex];
      if (item) goTo(item);
    }
  };

  // Role gate — only render on allowed roles
  if (allowedRoles.length === 0) return null;

  const totalCount = flatResults.length;

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          type="text"
          placeholder="Search distributors, parties, GST, mobile, city…  (Ctrl+K)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
          className="w-full h-9 pl-9 pr-9 rounded-md border border-input bg-white text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setData(null); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="global-search-clear"
            aria-label="Clear search"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          data-testid="global-search-dropdown"
          className="absolute z-50 mt-1 w-[min(680px,90vw)] right-0 md:left-0 bg-white border border-border rounded-lg shadow-lg max-h-[70vh] overflow-y-auto"
        >
          {loading && !data && (
            <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}

          {data && totalCount === 0 && !loading && (
            <div className="p-6 text-sm text-muted-foreground text-center" data-testid="global-search-empty">
              No results for “{q}”.
            </div>
          )}

          {data && totalCount > 0 && (
            <div className="py-2">
              {Object.entries(SECTION_META).map(([key, meta]) => {
                const items = data[key] || [];
                if (items.length === 0) return null;
                const Icon = meta.icon;
                return (
                  <div key={key} className="pb-2">
                    <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5 ${meta.color}`}>
                      <Icon className="w-3 h-3" />
                      {meta.label} <span className="text-muted-foreground font-normal">({items.length})</span>
                    </div>
                    {items.map((item) => {
                      const flatIdx = flatResults.findIndex(
                        (r) => r.__section === key && r.id === item.id
                      );
                      const active = flatIdx === activeIndex;
                      return (
                        <button
                          key={`${key}-${item.id}`}
                          type="button"
                          onClick={() => goTo({ ...item, __section: key })}
                          onMouseEnter={() => setActiveIndex(flatIdx)}
                          data-testid={`global-search-result-${key}-${item.id}`}
                          className={`w-full text-left px-3 py-2 text-sm border-l-2 ${
                            active ? "bg-muted border-primary" : "border-transparent hover:bg-muted/60"
                          } transition-colors`}
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-medium truncate">
                              {HIGHLIGHT(item.name || item.email || "—", q)}
                            </span>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {item.code || item.role || ""}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {[
                              item.gst && `GST: ${item.gst}`,
                              item.mobile && `Mob: ${item.mobile}`,
                              item.city && `${item.city}${item.state ? ", " + item.state : ""}`,
                              item.contact_person,
                              item.email,
                              item.registration_status,
                            ]
                              .filter(Boolean)
                              .map((s, i) => <React.Fragment key={i}>{i > 0 && " · "}{HIGHLIGHT(s, q)}</React.Fragment>)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
